import React, { useEffect, useState, useMemo } from "react";
import { Select, Typography } from "antd";
import { ConfigService } from "../../../services/ConfigService";
import { usePermissions } from "../../../PermissionsProvider";
import { ModelConfig } from "../../../model_gen/config";

interface ModelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    ConfigService.getModels(
      intentGroupId ?? companyId,
      setIsLoading,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];

        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }

        setModels(modelsToDisplay);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Helper functions from ConfigurationPanel
  const groupModelsByProvider = (models: string[]) => {
    const grouped: { [provider: string]: string[] } = {};
    models.forEach((model) => {
      const [provider] = model.split(":");
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      if (!grouped[provider].includes(model)) {
        grouped[provider].push(model);
      }
    });
    return grouped;
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Group and sort models - memoized to avoid recalculations on every render
  const { groupedModels, sortedProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(models);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedModels: grouped, sortedProviders: sorted };
  }, [models]);

  return (
    <div style={style}>
      {label && <Typography.Text strong>{label}</Typography.Text>}
      <Select
        value={value}
        style={{ width: "100%", marginTop: label ? "8px" : 0 }}
        className="h-56"
        onChange={onChange}
        disabled={disabled}
        loading={isLoading}
        placeholder={placeholder}
        optionLabelProp="label"
        optionFilterProp="children"
        showSearch>
        {showEmptyOption && (
          <Select.Option key="" value="" label="None">
            None
          </Select.Option>
        )}
        {sortedProviders.map((provider) => (
          <Select.OptGroup
            key={provider}
            label={
              provider.toLowerCase() === "maitai" ? (
                <Typography.Text strong style={{ color: "#21b892" }}>
                  {capitalizeFirstLetter(provider)} (Recommended)
                </Typography.Text>
              ) : (
                capitalizeFirstLetter(provider)
              )
            }>
            {groupedModels[provider].map((model) => {
              const [, ...modelParts] = model.split(":");
              const modelName = modelParts.join(":");
              return (
                <Select.Option key={model} value={model} label={model}>
                  {modelName}
                </Select.Option>
              );
            })}
          </Select.OptGroup>
        ))}
      </Select>
    </div>
  );
};

export default ModelInput;
